import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Home, Search, PlusCircle, User, Menu, X, LogOut } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className='sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm'>
      <div className='max-w-7xl mx-auto px-4 md:px-8'>
        <div className='flex items-center justify-between h-16'>
          {/* Logo */}
          <Link
            to='/'
            className='flex items-center space-x-2'
            data-testid='logo-link'
          >
            <Home className='w-8 h-8 text-primary' />
            <span className='text-2xl font-heading font-bold text-slate-900'>
              Propiedades<span className='text-primary'>CO</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className='hidden md:flex items-center space-x-8'>
            <Link
              to='/'
              className='text-slate-700 hover:text-primary font-medium transition-colors duration-200'
              data-testid='nav-home'
            >
              Inicio
            </Link>
            <Link
              to='/buscar?operation=SALE'
              className='text-slate-700 hover:text-primary font-medium transition-colors duration-200'
              data-testid='nav-buy'
            >
              Comprar
            </Link>
            <Link
              to='/buscar?operation=RENT'
              className='text-slate-700 hover:text-primary font-medium transition-colors duration-200'
              data-testid='nav-rent'
            >
              Arrendar
            </Link>
            {isAuthenticated && (
              <>
                <Link
                  to='/publicar-propiedad'
                  className='text-slate-700 hover:text-primary font-medium transition-colors duration-200'
                  data-testid='nav-publish'
                >
                  Publicar
                </Link>
                <Link
                  to='/pagos'
                  className='text-slate-700 hover:text-primary font-medium transition-colors duration-200'
                  data-testid='nav-payments'
                >
                  Planes
                </Link>
              </>
            )}
            {isAdmin && (
              <Link
                to='/admin'
                className='text-slate-700 hover:text-primary font-medium transition-colors duration-200'
                data-testid='nav-admin'
              >
                Admin
              </Link>
            )}
          </nav>

          {/* Desktop Auth */}
          <div className='hidden md:flex items-center space-x-4'>
            {isAuthenticated ? (
              <>
                <Link to='/mi-cuenta' data-testid='profile-button'>
                  <Button
                    variant='outline'
                    className='flex items-center space-x-2'
                  >
                    <User className='w-4 h-4' />
                    <span>{user?.name}</span>
                  </Button>
                </Link>
                <Button
                  onClick={handleLogout}
                  variant='outline'
                  size='icon'
                  data-testid='logout-button'
                >
                  <LogOut className='w-4 h-4' />
                </Button>
              </>
            ) : (
              <>
                <Link to='/iniciar-sesion' data-testid='login-button'>
                  <Button variant='outline'>Iniciar Sesión</Button>
                </Link>
                <Link to='/registro' data-testid='register-button'>
                  <Button className='bg-primary hover:bg-primary-hover'>
                    Registrarse
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className='md:hidden p-2 text-slate-700 hover:text-primary'
            data-testid='mobile-menu-button'
          >
            {mobileMenuOpen ? (
              <X className='w-6 h-6' />
            ) : (
              <Menu className='w-6 h-6' />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div
          className='md:hidden border-t border-slate-200 bg-white'
          data-testid='mobile-menu'
        >
          <div className='px-4 py-4 space-y-4'>
            <Link
              to='/'
              className='block text-slate-700 hover:text-primary font-medium'
              onClick={() => setMobileMenuOpen(false)}
            >
              Inicio
            </Link>
            <Link
              to='/buscar?operation=SALE'
              className='block text-slate-700 hover:text-primary font-medium'
              onClick={() => setMobileMenuOpen(false)}
            >
              Comprar
            </Link>
            <Link
              to='/buscar?operation=RENT'
              className='block text-slate-700 hover:text-primary font-medium'
              onClick={() => setMobileMenuOpen(false)}
            >
              Arrendar
            </Link>
            {isAuthenticated && (
              <>
                <Link
                  to='/publicar-propiedad'
                  className='block text-slate-700 hover:text-primary font-medium'
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Publicar
                </Link>
                <Link
                  to='/pagos'
                  className='block text-slate-700 hover:text-primary font-medium'
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Planes
                </Link>
              </>
            )}
            {isAdmin && (
              <Link
                to='/admin'
                className='block text-slate-700 hover:text-primary font-medium'
                onClick={() => setMobileMenuOpen(false)}
              >
                Admin
              </Link>
            )}
            <div className='pt-4 border-t border-slate-200 space-y-3'>
              {isAuthenticated ? (
                <>
                  <Link
                    to='/mi-cuenta'
                    className='block'
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Button className='w-full' variant='outline'>
                      <User className='w-4 h-4 mr-2' />
                      {user?.name}
                    </Button>
                  </Link>
                  <Button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className='w-full'
                    variant='outline'
                  >
                    <LogOut className='w-4 h-4 mr-2' />
                    Cerrar Sesión
                  </Button>
                </>
              ) : (
                <>
                  <Link
                    to='/iniciar-sesion'
                    className='block'
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Button className='w-full' variant='outline'>
                      Iniciar Sesión
                    </Button>
                  </Link>
                  <Link
                    to='/registro'
                    className='block'
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Button className='w-full bg-primary hover:bg-primary-hover'>
                      Registrarse
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
